import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  Legend,
} from 'recharts';
import type { AMChartEntry } from '../../../utils/accountOwnerUtils';
import { fmtUSD } from '../../../utils/accountOwnerUtils';

interface ChartsSectionProps {
  chartData: AMChartEntry[];
}

// ─── Shared colors ────────────────────────────────────────────────────────────
const BLUE = '#2563EB';
const EMERALD = '#059669';
const AMBER = '#D97706';
const RED = '#DC2626';

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  formatter?: (val: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, formatter }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-[12px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full flex-none" style={{ background: p.color }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold text-slate-800">
            {formatter ? formatter(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Chart card wrapper ───────────────────────────────────────────────────────
const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md p-4">
    <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-3">{title}</p>
    {children}
  </div>
);

// ─── Tick formatters ──────────────────────────────────────────────────────────
const fmtUSDAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

const fmtHoursAxis = (v: number) => {
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return `${v}`;
};

// ─── Main component ───────────────────────────────────────────────────────────
export const ChartsSection: React.FC<ChartsSectionProps> = ({ chartData }) => {
  if (chartData.length === 0) {
    return (
      <section className="space-y-3" aria-label="Portfolio analytics charts">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-1">
          Analytics
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {['Revenue by AM', 'Margin % by AM', 'Billable Hours by AM', 'Cost vs Revenue by AM'].map(t => (
            <ChartCard key={t} title={t}>
              <div className="h-[220px] flex items-center justify-center">
                <p className="text-slate-600 text-sm">No data for selected period</p>
              </div>
            </ChartCard>
          ))}
        </div>
      </section>
    );
  }

  // Limit to top 10 AMs by revenue to keep charts readable
  const displayData = chartData.slice(0, 10);

  return (
    <section className="space-y-3" aria-label="Portfolio analytics charts">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 px-1">
        Analytics
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

      {/* 1. Revenue by Account Owner — Horizontal BarChart */}
      <ChartCard title="Revenue by Account Owner">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={displayData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis
              type="number"
              tickFormatter={fmtUSDAxis}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              tick={{ fontSize: 11, fill: '#334155' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={
                <CustomTooltip formatter={(v) => fmtUSD(v)} />
              }
            />
            <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]} maxBarSize={20}>
              {displayData.map((_, idx) => (
                <Cell key={idx} fill={BLUE} fillOpacity={1 - idx * 0.06} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* 2. Margin % by Account Owner — Vertical BarChart */}
      <ChartCard title="Margin % by Account Owner">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={displayData} margin={{ left: 4, right: 4, top: 4, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: '#334155' }}
              axisLine={false}
              tickLine={false}
              angle={-30}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={
                <CustomTooltip formatter={(v) => `${v}%`} />
              }
            />
            <Bar dataKey="marginPct" name="Margin %" radius={[4, 4, 0, 0]} maxBarSize={28}>
              {displayData.map((entry, idx) => (
                <Cell
                  key={idx}
                  fill={(entry.marginPct ?? 0) >= 0 ? EMERALD : RED}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* 3. Billable Hours by Account Owner — Horizontal BarChart */}
      <ChartCard title="Billable Hours by Account Owner">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={displayData} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <XAxis
              type="number"
              tickFormatter={fmtHoursAxis}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              tick={{ fontSize: 11, fill: '#334155' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={
                <CustomTooltip formatter={(v) => `${Math.round(v).toLocaleString('en-US')}h`} />
              }
            />
            <Bar dataKey="billable" name="Billable hrs" radius={[0, 4, 4, 0]} maxBarSize={20}>
              {displayData.map((_, idx) => (
                <Cell key={idx} fill="#64748b" fillOpacity={1 - idx * 0.06} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* 4. Cost vs Revenue — Grouped BarChart */}
      <ChartCard title="Cost vs Revenue by Account Owner">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={displayData} margin={{ left: 4, right: 4, top: 4, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10, fill: '#334155' }}
              axisLine={false}
              tickLine={false}
              angle={-30}
              textAnchor="end"
              interval={0}
            />
            <YAxis
              tickFormatter={fmtUSDAxis}
              tick={{ fontSize: 10, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={
                <CustomTooltip formatter={(v) => fmtUSD(v)} />
              }
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
            />
            <Bar dataKey="revenue" name="Revenue" fill={BLUE} radius={[4, 4, 0, 0]} maxBarSize={14} />
            <Bar dataKey="cost" name="Cost" fill={AMBER} radius={[4, 4, 0, 0]} maxBarSize={14} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      </div>
    </section>
  );
};

export default ChartsSection;
